import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import Input from "../../../components/elements/form/input";
import Button from '../../../components/elements/button';
import { useSagesApi } from '../../../store/api/hooks/sageApiHook';
import toast from 'react-hot-toast';
import { useAppLoader } from '../../../_common/pageLoader/appLoaderHook';
import SelectBox from '../../../components/elements/form/selectBox';
import { useAssetsApi } from '../../../store/api/hooks/assetsApiHook';

interface AssetsGroupMasterFormProps {
    _ref?: any;
    editData?: any;
    onCreate?: () => void;
    onCancel?: () => void;
}

type AssetGroupMasterFormValueType = {
    MapID?: any;
    assetSubGroupID?: any;
    accountGroupID?: any;
    accountID?: any;
    accmDepreciationAccountID?: any;
    revaluationAccountID?: any;
    deprExpenseAccountID?: any;
};

const AddAssestsDataMapping: React.FC<AssetsGroupMasterFormProps> = ({
    _ref,
    editData,
    onCreate = () => { },
    onCancel = () => { }
}) => {
    const { control, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm<AssetGroupMasterFormValueType>({
        defaultValues: {}
    });

    const sageApi: any = useSagesApi();
    const assetsAPI = useAssetsApi();
    const { showLoader, hideLoader } = useAppLoader();

    const [assetGroupOption, setassetGroupOption] = useState<any>(null);
    const [accGroupOption, setaccGroupOption] = useState<any>(null);
    const [accOption, setaccOption] = useState<any>(null);
    const [accmDepreciationOption, setaccmDepreciationOption] = useState<any>(null);
    const [accRevaluationOption, setaccRevaluationOption] = useState<any>(null);
    const [DeprExpenseOption, setDeprExpenseOption] = useState<any>(null);

    const selectedAccountGroup = watch('accountGroupID');

    const formReset = () => {
        reset({
            MapID: editData ? editData.mapID : '',
            assetSubGroupID: '',
            accountGroupID: '',
            accountID: '',
            accmDepreciationAccountID: '',
            revaluationAccountID: '',
            deprExpenseAccountID: 30
        });
        
        // Set default value for deprExpenseAccountID after reset with a small delay
        setTimeout(() => {
            if (DeprExpenseOption?.length > 0 && !editData) {
                const defaultOption = DeprExpenseOption.find((opt: any) => opt.value === 30);
                if (defaultOption) {
                    setValue('deprExpenseAccountID', defaultOption);
                }
            }
        }, 100);
    };

    const onSubmit: SubmitHandler<AssetGroupMasterFormValueType> = formData => {
        showLoader();
        const payload: any = {
            assetSubGroupID: formData.assetSubGroupID?.value,
            accountGroupID: formData.accountGroupID?.value,
            accountID: formData.accountID?.value,
            accmDepreciationAccountID: formData.accmDepreciationAccountID?.value,
            revaluationAccountID: formData.revaluationAccountID?.value,
            deprExpenseAccountID: formData.deprExpenseAccountID?.value || 30 // Default to 30 if blank
        };

        if (editData) {
            payload['MapID'] = editData.mapID;
        }

        sageApi.saveAssetMapping(payload, (resp: any) => {
            if (resp.successful) {
                onCreate();
                onCancel();
                formReset();
                hideLoader();
                toast.success(resp?.message);
            } else {
                hideLoader();
                toast.error(resp.message || "Connection Failed");
            }
        }, (error: any) => {
            hideLoader();
            toast.error(error);
        });
    };

    const getAssetGrouplist = () => {
        assetsAPI.getAssetSubGrouplist({}, (resp: any) => {
            const options = resp.assetSubGroupList?.map((assetgroup: any) => ({
                label: assetgroup.assetSubGroupName,
                value: assetgroup.assetSubGroupID
            })) || [];
            setassetGroupOption(options);
        }, (error: any) => toast.error(error));
    };

    const getAccountGrouplist = () => {
        sageApi.getSyncAccountGroups({ pageSize: 0 }, (resp: any) => {
            const options = resp.sageAccountGroupList?.map((group: any) => ({
                label: group.accountGroupName,
                value: group.accountGroupID
            })) || [];
            setaccGroupOption(options);
        }, (error: any) => toast.error(error));
    };

    const getAccountList = (accountGroupID: number) => {
        if (!accountGroupID) {
            setaccOption([]);
            return;
        }
        sageApi.getSyncAccount({ pageSize: 0, accountGroupID }, (resp: any) => {
            const options = resp.sageAccountList?.map((acc: any) => ({
                label: `${acc.accountNo}--${acc.accountTitle}`,
                value: acc.accountID
            })) || [];
            setaccOption(options);
        }, (error: any) => toast.error(error));
    };

    const getAccmDepreciation = () => {
        sageApi.getSyncAccount({ pageSize: 0 }, (resp: any) => {
            const options = resp.sageAccountList?.map((acc: any) => ({
                label: `${acc.accountNo}--${acc.accountTitle}`,
                value: acc.accountID
            })) || [];
            setaccmDepreciationOption(options);
        }, (error: any) => toast.error(error));
    };

    const getAccountRevaluationList = () => {
        sageApi.getSyncAccount({ pageSize: 0 }, (resp: any) => {
            const options = resp.sageAccountList?.map((acc: any) => ({
                label: `${acc.accountNo}--${acc.accountTitle}`,
                value: acc.accountID
            })) || [];
            setaccRevaluationOption(options);
        }, (error: any) => toast.error(error));
    };

    const getDeprExpenseList = () => {
        sageApi.getSyncAccount({ pageSize: 0 }, (resp: any) => {
            const options = resp.sageAccountList?.map((acc: any) => ({
                label: `${acc.accountNo}--${acc.accountTitle}`,
                value: acc.accountID
            })) || [];
            setDeprExpenseOption(options);
        }, (error: any) => toast.error(error));
    };

    useEffect(() => {
        if (selectedAccountGroup?.value) {
            getAccountList(selectedAccountGroup.value);
        } else {
            setaccOption([]);
        }
    }, [selectedAccountGroup]);


    useEffect(() => {
        getAssetGrouplist();
        getAccountGrouplist();
        getAccmDepreciation();
        getAccountRevaluationList();
        getDeprExpenseList();
    }, []);

    useEffect(() => {
        if (_ref?.current) {
            _ref.current.reset = formReset;
        }
    }, [_ref]);

    useEffect(() => {
        if (editData == null) {
            formReset();
        }
    }, [editData]);

    // Handle deprExpenseAccountID setting for both edit and create modes
    useEffect(() => {
        if (DeprExpenseOption?.length > 0) {
            if (editData?.deprExpenseAccountID) {
                // Edit mode - set the saved value
                const selected = DeprExpenseOption.find((opt: any) => opt.value === editData.deprExpenseAccountID);
                if (selected) {
                    setValue('deprExpenseAccountID', selected);
                }
            } else {
                // Create mode or no saved value - set default to ID 30
                const defaultOption = DeprExpenseOption.find((opt: any) => opt.value === 30);
                if (defaultOption) {
                    setValue('deprExpenseAccountID', defaultOption);
                }
            }
        }
    }, [DeprExpenseOption, editData]);

    /* useEffect(() => {
        if (editData && assetGroupOption && accGroupOption && accOption && accmDepreciationOption && accRevaluationOption) {
            const findOption = (options: any[], id: any) => options?.find(opt => opt.value === id) || null;

            setValue('assetSubGroupID', findOption(assetGroupOption, editData.assetSubGroupID));
            setValue('accountGroupID', findOption(accGroupOption, editData.accountGroupID));
            setValue('accountID', findOption(accOption, editData.accountID));
            setValue('accmDepreciationAccountID', findOption(accmDepreciationOption, editData.accmDepreciationAccountID));
            setValue('revaluationAccountID', findOption(accRevaluationOption, editData.revaluationAccountID));
        }
    }, [
        editData,
        assetGroupOption,
        accGroupOption,
        accOption,
        accmDepreciationOption,
        accRevaluationOption
    ]); */


    // Step 1: Set assetSubGroupID and accountGroupID first
    useEffect(() => {
        if (editData && assetGroupOption && accGroupOption) {
            const findOption = (options: any[], id: any) => options?.find(opt => opt.value === id) || null;

            const assetSubGroup = findOption(assetGroupOption, editData.assetSubGroupID);
            const accountGroup = findOption(accGroupOption, editData.accountGroupID);

            setValue('assetSubGroupID', assetSubGroup);
            setValue('accountGroupID', accountGroup);
        }
    }, [editData, assetGroupOption, accGroupOption]);

    // Step 2: Once accountGroupID is set from editData, fetch account list
    useEffect(() => {
        if (editData?.accountGroupID) {
            getAccountList(editData.accountGroupID);
        }
    }, [editData?.accountGroupID]);

    // Step 3: Once account options are ready, set accountID
    useEffect(() => {
        if (editData?.accountID && accOption?.length > 0) {
            const selected = accOption.find((opt: any) => opt.value === editData.accountID);
            if (selected) {
                setValue('accountID', selected);
            }
        }
    }, [accOption]);

    // Step 4: Set accmDepreciationAccountID and revaluationAccountID
    useEffect(() => {
        if (editData && accmDepreciationOption && accRevaluationOption) {
            const findOption = (options: any[], id: any) => options?.find(opt => opt.value === id) || null;

            setValue('accmDepreciationAccountID', findOption(accmDepreciationOption, editData.accmDepreciationAccountID));
            setValue('revaluationAccountID', findOption(accRevaluationOption, editData.revaluationAccountID));
        }
    }, [editData, accmDepreciationOption, accRevaluationOption]);

    return (
        <form onSubmit={handleSubmit(onSubmit)} ref={_ref} className="space-y-5">
            <Controller
                name="assetSubGroupID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Select Assets Group'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Assets Group'
                        options={assetGroupOption}
                        error={errors?.assetSubGroupID?.message as any}
                    />
                )}
            />
            <Controller
                name="accountGroupID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Account Group'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Account Group'
                        options={accGroupOption}
                        error={errors?.accountGroupID?.message as any}
                    />
                )}
            />
            <Controller
                name="accountID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Account'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Account'
                        options={accOption}
                        error={errors?.accountID?.message as any}
                    />
                )}
            />
            <Controller
                name="accmDepreciationAccountID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Accm Depreciation'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Accm Depreciation'
                        options={accmDepreciationOption}
                        error={errors?.accmDepreciationAccountID?.message as any}
                    />
                )}
            />
            {/* <Controller
                name="revaluationAccountID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Revaluation Reserve / Surplus Account'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Revaluation Reserve / Surplus Account'
                        options={accRevaluationOption}
                        error={errors?.revaluationAccountID?.message as any}
                    />
                )}
            /> */}
            <Controller
                name="deprExpenseAccountID"
                control={control}
                render={({ field: { value, onChange } }) => (
                    <SelectBox
                        label='Depreciation Expense Account'
                        value={value}
                        onChange={onChange}
                        placeholder='Choose Depreciation Expense Account'
                        options={DeprExpenseOption}
                        error={errors?.deprExpenseAccountID?.message as any}
                    />
                )}
            />

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                <Button type="button" theme="secondary" onClick={formReset}>
                    Reset
                </Button>
                <Button type="submit">Save</Button>
            </div>
        </form>
    );
};

export default AddAssestsDataMapping;